import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCare } from '../contexts/CareContext'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import { todayISO } from '../lib/tasks'
import QuickAddClientSheet from '../components/admin/QuickAddClientSheet'
import CaregiverList from '../components/admin/CaregiverList'

const STAGE_LABEL = { early: 'Early stage', middle: 'Middle stage', late: 'Late stage', unknown: null }

const FILTERS = [
  ['all', 'All'],
  ['attention', 'Needs attention'],
  ['nolog', 'No log today'],
]

/**
 * Coordinator home base — every client a user is admin on, in one list.
 *
 * One round trip per table across all clients rather than per-client queries,
 * so the list stays fast as the roster grows.
 */
export default function AdminDashboardPage() {
  const { adminMemberships, setActiveId, isAdminAnywhere } = useCare()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState('all')
  const [addOpen, setAddOpen] = useState(false)
  const scrolledToHash = useRef(false)

  // More → Caregivers links to /admin#caregivers, so honour the hash once the
  // section has actually rendered.
  useEffect(() => {
    if (scrolledToHash.current || loading) return
    if (window.location.hash !== '#caregivers') return
    scrolledToHash.current = true
    requestAnimationFrame(() => {
      document.getElementById('caregivers')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [loading])

  const ids = adminMemberships.map((m) => m.recipient.id)

  const load = useCallback(async () => {
    if (!ids.length) { setLoading(false); return }
    const today = todayISO()

    const [logsRes, medsRes, medLogsRes, incRes, teamRes, taskRes] = await Promise.all([
      supabase.from('care_logs')
        .select('care_recipient_id, shift, mood_morning, mood_afternoon, mood_evening, logged_by, logger_type')
        .in('care_recipient_id', ids).eq('log_date', today),
      supabase.from('medications').select('id, care_recipient_id')
        .in('care_recipient_id', ids).eq('is_active', true),
      supabase.from('medication_logs').select('care_recipient_id, medication_id, status')
        .in('care_recipient_id', ids).eq('log_date', today),
      supabase.from('incidents').select('id, care_recipient_id')
        .in('care_recipient_id', ids).eq('incident_date', today),
      supabase.from('care_team_members')
        .select('care_recipient_id, role, relationship, invited_name, profile:profiles(full_name)')
        .in('care_recipient_id', ids).eq('is_active', true),
      supabase.from('tasks').select('care_recipient_id, title, due_date, is_completed')
        .in('care_recipient_id', ids).eq('is_completed', false).lte('due_date', today),
    ])

    const by = (arr, key = 'care_recipient_id') => {
      const m = new Map()
      for (const r of arr || []) {
        if (!m.has(r[key])) m.set(r[key], [])
        m.get(r[key]).push(r)
      }
      return m
    }

    const logs = by(logsRes.data), meds = by(medsRes.data), medLogs = by(medLogsRes.data)
    const incs = by(incRes.data), team = by(teamRes.data), tasks = by(taskRes.data)

    setRows(adminMemberships.map(({ recipient }) => {
      const id = recipient.id
      const todayLogs = logs.get(id) || []
      const moods = todayLogs.flatMap((l) => [l.mood_morning, l.mood_afternoon, l.mood_evening]).filter((v) => v != null)
      const members = team.get(id) || []
      const caregivers = members.filter((m) => m.role === 'caregiver')
      const family = members.filter((m) => m.role === 'admin' || m.role === 'family_member')
      const overdue = (tasks.get(id) || []).filter((t) => t.due_date < today)
      const incidents = (incs.get(id) || []).length

      return {
        recipient,
        logged: todayLogs.length > 0,
        shifts: todayLogs.map((l) => l.shift).filter(Boolean),
        moodAvg: moods.length ? Math.round((moods.reduce((a, b) => a + b, 0) / moods.length) * 10) / 10 : null,
        medsTotal: (meds.get(id) || []).length,
        medsTaken: (medLogs.get(id) || []).filter((l) => l.status === 'taken').length,
        incidents,
        overdue,
        caregiver: caregivers[0]
          ? (caregivers[0].profile?.full_name || caregivers[0].invited_name || 'Assigned')
          : null,
        familyCount: family.length,
      }
    }))
    setLoading(false)
  }, [adminMemberships]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load() }, [load])

  // Reached by URL without being admin anywhere — explain rather than 404.
  if (!isAdminAnywhere) {
    return (
      <div className="stack">
        <h1 className="title">All clients</h1>
        <div className="card muted" style={{ fontSize: 14, lineHeight: 1.55 }}>
          This overview is for coordinators. You&rsquo;ll see it here once
          you&rsquo;re an admin on a care record.
        </div>
        <button className="btn btn-block" onClick={() => navigate('/')}>Back to home</button>
      </div>
    )
  }

  if (loading) return <LoadingSpinner label="Loading clients…" />

  const tone = (r) => {
    if (!r.logged || r.incidents > 0) return 'red'
    if (r.overdue.length > 0) return 'amber'
    return 'green'
  }
  const BORDER = { red: 'var(--danger)', amber: 'var(--amber)', green: 'var(--green)' }

  const shown = rows.filter((r) => {
    const name = `${r.recipient.full_name} ${r.recipient.preferred_name || ''}`.toLowerCase()
    if (q.trim() && !name.includes(q.trim().toLowerCase())) return false
    if (filter === 'nolog') return !r.logged
    if (filter === 'attention') return tone(r) !== 'green'
    return true
  })

  function openClient(id) {
    setActiveId(id)
    navigate('/')
  }

  return (
    <div className="stack">
      <div className="spread">
        <h1 className="title">All clients</h1>
        <button className="btn btn-sm" onClick={() => setAddOpen(true)}>+ Add client</button>
      </div>

      {/* Search and filters only earn their space once there are several
          clients to sift through. */}
      {rows.length > 1 && (
        <>
          <input
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search clients…"
            aria-label="Search clients"
          />

          <div className="row wrap" style={{ gap: 8 }}>
            {FILTERS.map(([v, l]) => (
              <button
                key={v} type="button"
                className={`chip${filter === v ? ' chip-on' : ''}`}
                aria-pressed={filter === v}
                onClick={() => setFilter(v)}
              >
                {l}
              </button>
            ))}
          </div>
        </>
      )}

      {shown.length === 0 ? (
        <div className="card muted" style={{ fontSize: 14 }}>
          {rows.length === 0 ? 'No clients yet.' : 'No clients match that filter.'}
        </div>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {shown.map((r) => {
            const rc = r.recipient
            const stage = STAGE_LABEL[rc.care_stage]
            const t = tone(r)
            return (
              <button
                key={rc.id}
                onClick={() => openClient(rc.id)}
                className="card card-tap"
                style={{
                  width: '100%', textAlign: 'left',
                  borderColor: BORDER[t], borderWidth: 1.5,
                }}
              >
                <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                  <div
                    aria-hidden
                    style={{
                      width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                      background: 'var(--pa)', color: '#fff', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {(rc.full_name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>
                      {rc.preferred_name || rc.full_name}
                      {stage && <span className="muted" style={{ fontWeight: 400 }}> · {stage}</span>}
                    </div>

                    <div style={{ fontSize: 14, marginTop: 6 }}>
                      {r.logged
                        ? <>✓ {r.shifts.length ? `${r.shifts[0]} shift logged` : 'Logged today'}</>
                        : <span style={{ color: 'var(--danger)' }}>✗ No shift log yet</span>}
                    </div>

                    {r.logged && (
                      <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                        {[
                          r.moodAvg != null && `Mood ${r.moodAvg}/5`,
                          r.medsTotal > 0 && `${r.medsTaken}/${r.medsTotal} meds`,
                          r.incidents === 0 ? 'No incidents' : `${r.incidents} incident${r.incidents > 1 ? 's' : ''}`,
                        ].filter(Boolean).join(' · ')}
                      </div>
                    )}

                    <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
                      {!r.logged && <span className="badge badge-danger">🔴 No care log today</span>}
                      {r.incidents > 0 && <span className="badge badge-danger">⚠️ {r.incidents} incident{r.incidents > 1 ? 's' : ''}</span>}
                      {r.overdue.length > 0 && (
                        <span className="badge badge-amber">⚠️ {r.overdue.length} overdue task{r.overdue.length > 1 ? 's' : ''}</span>
                      )}
                    </div>

                    <div className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.6 }}>
                      Caregiver: {r.caregiver || 'None assigned'}<br />
                      Family: {r.familyCount} member{r.familyCount === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}

      {/* With a single client the header button is easy to miss, so the
          invitation to add another gets real weight here. */}
      {rows.length <= 1 && (
        <button
          onClick={() => setAddOpen(true)}
          style={{
            border: '1.5px dashed var(--pa)', borderRadius: 'var(--radius)',
            background: 'var(--pa-light)', padding: 18, width: '100%',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 22, lineHeight: 1.2 }} aria-hidden>＋</span>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--teal-dark)' }}>Add another client</div>
              <div className="muted" style={{ fontSize: 13, marginTop: 2, lineHeight: 1.5 }}>
                {rows.length === 0
                  ? 'Set up your first care recipient with Ginie.'
                  : 'Ginie will walk you through setting up a new care recipient.'}
              </div>
            </div>
          </div>
        </button>
      )}

      <CaregiverList adminMemberships={adminMemberships} />

      {addOpen && (
        <QuickAddClientSheet
          onCancel={() => setAddOpen(false)}
          onDone={() => setAddOpen(false)}
        />
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}
